import { FC, useState } from 'react'
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Placement } from '@popperjs/core'

import { ISafetyCaseRiskMatrix, ISafetyCaseRiskMatrixCell } from '../../../interfaces/services/safety-case-risk-matrix'

import { calcFontColor } from '../../../util/font-color-calculator'

import s from './risk-matrix-picker.module.scss'

interface RiskMatrixTableProps {
  selectedCell?: ISafetyCaseRiskMatrixCell
  data?: ISafetyCaseRiskMatrix
  onChange: (cell: ISafetyCaseRiskMatrixCell) => void
  isOpen: boolean
  closeModal: () => void
}

type HintIconProps = {
  hint?: string
  tooltipId: string
  placement: Placement
  ariaLabel: string
}

// Tapping the icon always opens the tooltip and tapping elsewhere closes it
const HintIcon: FC<HintIconProps> = ({ hint, tooltipId, placement, ariaLabel }) => {
  const [show, setShow] = useState(false)

  if (!hint) {
    return null
  }

  return (
    <OverlayTrigger
      show={show}
      onToggle={setShow}
      rootClose
      trigger={[]}
      placement={placement}
      transition={false}
      overlay={<Tooltip id={tooltipId}>{hint}</Tooltip>}
    >
      <i
        className={`bi bi-info-circle ml-1 ${s['hint-icon']}`}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        // Ignore pointer events for non-mouse inputs to prevent flickering tooltips on touch devices
        onPointerEnter={e => e.pointerType === 'mouse' && setShow(true)}
        onPointerLeave={e => e.pointerType === 'mouse' && setShow(false)}
        // Only show for keyboard tabbing (which has a previous element); focus restored on returning to the tab has none
        onFocus={e => e.relatedTarget && setShow(true)}
        onBlur={() => setShow(false)}
        onClick={e => {
          e.stopPropagation() // otherwise rootClose sees this tap as an outside click and closes the tooltip
          setShow(true)
        }}
      />
    </OverlayTrigger>
  )
}

type RiskCellProps = {
  cell?: ISafetyCaseRiskMatrixCell
  tooltipId: string
  isSelected: boolean
  onSelect: (cell: ISafetyCaseRiskMatrixCell) => void
}

const RiskCell: FC<RiskCellProps> = ({ cell, tooltipId, isSelected, onSelect }) => {
  const [show, setShow] = useState(false)

  return (
    <OverlayTrigger
      show={show}
      trigger={[]}
      placement="top"
      transition={false}
      overlay={<Tooltip id={tooltipId}>{cell?.riskHint || 'Select risk level'}</Tooltip>}
    >
      <td
        className={isSelected ? s['selected'] : ''}
        style={{
          backgroundColor: cell ? cell.riskColor : 'transparent',
          color: cell ? calcFontColor(cell.riskColor) : 'inherit',
        }}
        // Ignore pointer events for non-mouse inputs to prevent flickering tooltips on touch devices
        onPointerEnter={e => e.pointerType === 'mouse' && setShow(true)}
        onPointerLeave={e => e.pointerType === 'mouse' && setShow(false)}
        onClick={() => cell && onSelect(cell)}
      >
        {cell?.riskLabel ?? ''}
      </td>
    </OverlayTrigger>
  )
}

const RiskMatrixModal = ({ isOpen, closeModal, selectedCell, data, onChange }: RiskMatrixTableProps) => {
  const severities = data?.severities ? [...data.severities].sort((a, b) => a.level - b.level) : []
  const probabilities = data?.probabilities ? [...data.probabilities].sort((a, b) => b.level - a.level) : []

  return (
    <Modal
      animation={false}
      onHide={closeModal}
      show={isOpen}
      centered
      size="lg"
      scrollable={true}
      className={s['risk-matrix-modal']}
    >
      <Modal.Header closeButton>
        <Modal.Title>Select Risk Level</Modal.Title>
      </Modal.Header>
      <Modal.Body className={s['risk-matrix-body']}>
        <div>
          <table>
            <thead>
              <tr>
                <th></th>
                {severities.map(severity => (
                  <th key={severity.label} className="pb-1">
                    {severity.label}
                    <HintIcon
                      hint={severity.hint}
                      tooltipId={`severity-hint-${severity.level}`}
                      placement="top"
                      ariaLabel={`${severity.label} severity hint`}
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th rowSpan={probabilities.length + 1} className="font-size-lg !important pr-4">
                  <span className={s['y-axis-label']}>Probability</span>
                </th>
              </tr>
              {probabilities.map(probability => (
                <tr key={probability.label}>
                  {severities.map(severity => {
                    const cell = data?.cells.find(
                      c => c.probabilityLabel === probability.label && c.severityLabel === severity.label,
                    )

                    return (
                      <RiskCell
                        key={severity.label}
                        cell={cell}
                        tooltipId={`risk-hint-${cell?.id ?? `${severity.label}-${probability.label}`}`}
                        isSelected={selectedCell?.id === cell?.id}
                        onSelect={onChange}
                      />
                    )
                  })}
                  <th className="pl-1 text-left !important">
                    {probability.label}
                    <HintIcon
                      hint={probability.hint}
                      tooltipId={`probability-hint-${probability.level}`}
                      placement="bottom"
                      ariaLabel={`${probability.label} probability hint`}
                    />
                  </th>
                </tr>
              ))}
            </tbody>
            <thead>
              <tr>
                <th colSpan={severities.length + 2} className="font-size-lg !important pt-4">
                  Severity
                </th>
              </tr>
            </thead>
          </table>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default RiskMatrixModal
